import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Search, Loader2, UserPlus, Users, FileText,
  Building2, Zap, Flame, Wifi, Clock, X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const RECENT_KEY = 'crm_recent_searches';
const RECENT_MAX = 5;

const loadRecent = () => {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.slice(0, RECENT_MAX) : [];
  } catch (e) { return []; }
};

const saveRecent = (entry) => {
  try {
    const cur = loadRecent().filter(e => !(e.type === entry.type && e.id === entry.id));
    const next = [entry, ...cur].slice(0, RECENT_MAX);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
    return next;
  } catch (e) { return []; }
};

/**
 * Global search dropdown for header.
 * Debounced query -> /api/search/global -> grouped results (leads, clients, contracts).
 */
const GlobalSearch = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState({ leads: [], clients: [], contracts: [] });
  const [activeIdx, setActiveIdx] = useState(-1);
  const [recent, setRecent] = useState(() => loadRecent());
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  // Debounced fetch
  const fetchResults = useCallback(async (q) => {
    if (!q || q.trim().length < 2) {
      setResults({ leads: [], clients: [], contracts: [] });
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/search/global`, {
        params: { q, limit: 6 },
      });
      setResults({
        leads: res.data.leads || [],
        clients: res.data.clients || [],
        contracts: res.data.contracts || [],
      });
    } catch (e) {
      setResults({ leads: [], clients: [], contracts: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query || query.trim().length < 2) {
      setResults({ leads: [], clients: [], contracts: [] });
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(() => fetchResults(query), 280);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, fetchResults]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Global keyboard shortcut: Ctrl/Cmd + K
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        const input = containerRef.current?.querySelector('input');
        input?.focus();
        setOpen(true);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const flatItems = [
    ...results.leads.map(l => ({ type: 'lead', item: l })),
    ...results.clients.map(c => ({ type: 'client', item: c })),
    ...results.contracts.map(c => ({ type: 'contract', item: c })),
  ];

  const navigateItem = (entry) => {
    if (!entry) return;
    // Persist in recent history
    const recentEntry = {
      type: entry.type,
      id: entry.item.id,
      label: entry.type === 'lead' ? `${entry.item.nome || ''} ${entry.item.cognome || ''}`.trim()
        : entry.type === 'client' ? (entry.item.ragione_sociale || `${entry.item.nome || ''} ${entry.item.cognome || ''}`.trim())
        : (entry.item.numero_contratto ? `#${entry.item.numero_contratto}` : (entry.item.offerta_nome || entry.item.pod || entry.item.pdr || 'Pratica')),
      subtitle: entry.type === 'lead' ? (entry.item.telefono || entry.item.email || '')
        : entry.type === 'client' ? (entry.item.telefono || entry.item.email || '')
        : (entry.item.fornitore_nome || entry.item.stato || ''),
      badge: entry.type === 'lead' ? entry.item.stato
        : entry.type === 'client' ? entry.item.tipo_cliente
        : entry.item.tipologia,
      ts: Date.now(),
    };
    setRecent(saveRecent(recentEntry));

    setOpen(false);
    setQuery('');
    if (entry.type === 'lead') navigate(`/leads/${entry.item.id}`);
    else if (entry.type === 'client') navigate(`/clients/${entry.item.id}`);
    else if (entry.type === 'contract') navigate(`/contracts/${entry.item.id}`);
  };

  const openRecent = (r) => {
    setOpen(false);
    setQuery('');
    // Bump this entry to the top on click
    setRecent(saveRecent({ ...r, ts: Date.now() }));
    if (r.type === 'lead') navigate(`/leads/${r.id}`);
    else if (r.type === 'client') navigate(`/clients/${r.id}`);
    else if (r.type === 'contract') navigate(`/contracts/${r.id}`);
  };

  const clearRecent = (e) => {
    e.stopPropagation();
    try { localStorage.removeItem(RECENT_KEY); } catch (err) {}
    setRecent([]);
  };

  const handleKeyNav = (e) => {
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(i => Math.min(flatItems.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => Math.max(-1, i - 1));
    } else if (e.key === 'Enter' && activeIdx >= 0) {
      e.preventDefault();
      navigateItem(flatItems[activeIdx]);
    }
  };

  const totalResults = flatItems.length;
  const hasQuery = query.trim().length >= 2;
  const showResults = open && hasQuery;
  const showRecent = open && !hasQuery && recent.length > 0;

  const clientLabel = (c) => c.ragione_sociale || `${c.nome || ''} ${c.cognome || ''}`.trim() || '—';
  const leadLabel = (l) => `${l.nome || ''} ${l.cognome || ''}`.trim() || '—';

  const recentIcon = (type) => type === 'lead' ? UserPlus : type === 'client' ? Users : FileText;

  return (
    <div ref={containerRef} className="relative w-full max-w-md">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); setActiveIdx(-1); }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyNav}
          placeholder="Cerca lead, clienti, pratiche…"
          className="w-full h-9 pl-9 pr-16 rounded-lg border border-slate-200 bg-slate-50 focus:bg-white focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 text-sm placeholder:text-slate-400 outline-none transition-colors"
          data-testid="global-search-input"
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none flex items-center gap-1">
          {loading ? (
            <Loader2 className="w-3.5 h-3.5 text-slate-400 animate-spin" />
          ) : (
            <kbd className="hidden md:inline-flex items-center px-1.5 py-0.5 text-[10px] font-mono text-slate-400 bg-white border border-slate-200 rounded">
              Ctrl K
            </kbd>
          )}
        </span>
      </div>

      {showRecent && (
        <div
          className="absolute top-11 left-0 right-0 z-50 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden"
          data-testid="global-search-recent"
        >
          <div className="flex items-center gap-2 px-4 py-2 bg-slate-50 border-b border-slate-100">
            <Clock className="w-3.5 h-3.5 text-slate-500" />
            <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Ricerche recenti</span>
            <button
              onClick={clearRecent}
              className="ml-auto text-[11px] text-slate-400 hover:text-red-500 flex items-center gap-1 transition-colors"
              data-testid="clear-recent-btn"
            >
              <X className="w-3 h-3" /> Cancella
            </button>
          </div>
          <div>
            {recent.map((r) => {
              const Icon = recentIcon(r.type);
              return (
                <button
                  key={`${r.type}-${r.id}`}
                  type="button"
                  onClick={() => openRecent(r)}
                  className="w-full text-left px-4 py-2.5 flex items-center gap-3 hover:bg-slate-50 transition-colors border-b border-slate-50 last:border-0"
                  data-testid={`recent-${r.type}-${r.id}`}
                >
                  <span className="flex items-center justify-center w-7 h-7 rounded-md bg-slate-100 shrink-0">
                    <Icon className="w-3.5 h-3.5 text-slate-500" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{r.label || '—'}</p>
                    {r.subtitle && <p className="text-xs text-slate-500 truncate">{r.subtitle}</p>}
                  </div>
                  {r.badge && (
                    <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 uppercase tracking-wide shrink-0">
                      {r.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          <div className="border-t border-slate-100 px-3 py-2 bg-slate-50 text-[11px] text-slate-500">
            Inizia a digitare per cercare (min. 2 caratteri)
          </div>
        </div>
      )}

      {showResults && (
        <div
          className="absolute top-11 left-0 right-0 z-50 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden"
          data-testid="global-search-dropdown"
        >
          {loading && totalResults === 0 && (
            <div className="p-4 text-center text-slate-400 text-sm">Ricerca in corso…</div>
          )}
          {!loading && totalResults === 0 && (
            <div className="p-6 text-center">
              <Search className="w-8 h-8 mx-auto text-slate-300 mb-2" />
              <p className="text-sm text-slate-600 font-medium">Nessun risultato</p>
              <p className="text-xs text-slate-400 mt-1">Prova un altro termine (min. 2 caratteri)</p>
            </div>
          )}
          {totalResults > 0 && (
            <div className="max-h-[420px] overflow-y-auto">
              {/* Leads */}
              {results.leads.length > 0 && (
                <ResultGroup title="Lead" icon={UserPlus} count={results.leads.length}>
                  {results.leads.map((l, i) => {
                    const idx = i;
                    const isActive = activeIdx === idx;
                    return (
                      <ResultRow
                        key={l.id}
                        active={isActive}
                        onClick={() => navigateItem({ type: 'lead', item: l })}
                        title={leadLabel(l)}
                        subtitle={[l.telefono, l.email, l.stato].filter(Boolean).join(' • ')}
                        badge={l.stato}
                        testId={`search-lead-${l.id}`}
                      />
                    );
                  })}
                </ResultGroup>
              )}

              {/* Clients */}
              {results.clients.length > 0 && (
                <ResultGroup title="Clienti" icon={Users} count={results.clients.length}>
                  {results.clients.map((c, i) => {
                    const idx = results.leads.length + i;
                    const isActive = activeIdx === idx;
                    return (
                      <ResultRow
                        key={c.id}
                        active={isActive}
                        onClick={() => navigateItem({ type: 'client', item: c })}
                        title={clientLabel(c)}
                        subtitle={[c.telefono, c.email].filter(Boolean).join(' • ')}
                        badge={c.tipo_cliente}
                        testId={`search-client-${c.id}`}
                      />
                    );
                  })}
                </ResultGroup>
              )}

              {/* Contracts */}
              {results.contracts.length > 0 && (
                <ResultGroup title="Pratiche" icon={FileText} count={results.contracts.length}>
                  {results.contracts.map((c, i) => {
                    const idx = results.leads.length + results.clients.length + i;
                    const isActive = activeIdx === idx;
                    const TypeIcon = c.tipologia === 'gas' ? Flame : c.tipologia === 'internet' ? Wifi : Zap;
                    const label = c.numero_contratto ? `#${c.numero_contratto}` : (c.offerta_nome || c.pod || c.pdr || 'Pratica');
                    const sub = [c.cliente_label, c.fornitore_nome, c.stato].filter(Boolean).join(' • ');
                    return (
                      <ResultRow
                        key={c.id}
                        active={isActive}
                        onClick={() => navigateItem({ type: 'contract', item: c })}
                        title={label}
                        subtitle={sub}
                        badge={c.tipologia}
                        LeadIcon={TypeIcon}
                        testId={`search-contract-${c.id}`}
                      />
                    );
                  })}
                </ResultGroup>
              )}
            </div>
          )}
          <div className="border-t border-slate-100 px-3 py-2 flex items-center justify-between bg-slate-50 text-[11px] text-slate-500">
            <span>↑ ↓ Naviga · Enter Apri · Esc Chiudi</span>
            <span>{totalResults} risultati</span>
          </div>
        </div>
      )}
    </div>
  );
};

const ResultGroup = ({ title, icon: Icon, count, children }) => (
  <div>
    <div className="flex items-center gap-2 px-4 py-2 bg-slate-50 border-b border-slate-100">
      <Icon className="w-3.5 h-3.5 text-slate-500" />
      <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">{title}</span>
      <span className="ml-auto text-[10px] text-slate-400">{count}</span>
    </div>
    <div>{children}</div>
  </div>
);

const ResultRow = ({ active, onClick, title, subtitle, badge, LeadIcon, testId }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full text-left px-4 py-2.5 flex items-center gap-3 transition-colors border-b border-slate-50 last:border-0 ${
      active ? 'bg-sky-50' : 'hover:bg-slate-50'
    }`}
    data-testid={testId}
  >
    {LeadIcon && (
      <span className="flex items-center justify-center w-7 h-7 rounded-md bg-slate-100 shrink-0">
        <LeadIcon className="w-3.5 h-3.5 text-slate-500" />
      </span>
    )}
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-slate-800 truncate">{title}</p>
      {subtitle && <p className="text-xs text-slate-500 truncate">{subtitle}</p>}
    </div>
    {badge && (
      <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 uppercase tracking-wide shrink-0">
        {badge}
      </span>
    )}
  </button>
);

export default GlobalSearch;
